"use client";
import React, { useEffect } from "react";
import { IDecklistV2 } from "@/models/decklist";
import {
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  User,
  Chip,
  Tooltip,
  getKeyValue,
  Pagination,
  Link,
} from "@heroui/react";
import { getLeaderId } from "@/lib/leaderManager";
import { getLeaderSquare } from "@/lib/getLeaderImage";
import { LeaderSquareWithBorder } from "@/components/leaders/LeaderSquareWithBorder";
import { SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import { getDeckIdentity } from "@/lib/archetypeManager";

interface IDeckDistribution {
  [key: string]: {
    id: string;
    name: string;
    color: string;
    count: number;
    flagship: number;
    cs: number;
    sb: number;
    local: number;
    regional: number;
  };
}

export default function DeckDistribution({
  decks,
  game,
}: {
  decks: IDecklistV2[];
  game: SupportedGames;
}) {
  const gameHandler = getGameHandler(game);

  const items = React.useMemo(() => {
    let data: IDeckDistribution = {};
    for (const deck of decks) {
      const leaderId = deck.leader_id;
      // Use shared getDeckIdentity which automatically handles Palworld archetype derivation
      const leaderData = getDeckIdentity(game, leaderId, deck.decklist);

      if (!(leaderId in data)) {
        data[leaderId] = {
          id: leaderId,
          name: leaderData?.name || "",
          color: leaderData?.color || "",
          count: 0,
          flagship: 0,
          cs: 0,
          sb: 0,
          local: 0,
          regional: 0,
        };
      }
      data[leaderId].count = data[leaderId].count + 1;
      switch (deck.tournament_type) {
        case "Standard Battle":
          data[leaderId].sb += 1;
          break;
        case "CS":
          data[leaderId].cs += 1;
          break;
        case "Flagship":
          data[leaderId].flagship += 1;
          break;
        case "Regional":
          data[leaderId].regional += 1;
          break;
        case "Local":
          data[leaderId].local += 1;
          break;
        default:
          break;
      }
    }
    const valuesArray = Object.values(data);
    return valuesArray.sort((a, b) => b.count - a.count);
  }, [decks, gameHandler]);

  let totalCount = 0;
  for (const item of items) {
    totalCount = totalCount + item.count;
  }

  const renderCell = React.useCallback(
    (item: any, columnKey: string) => {
      const cellValue = item[columnKey as keyof typeof item];
      switch (columnKey) {
        case "name":
          const gameHandler = getGameHandler(game);
          const leaderData = gameHandler.getLeaderData(item.id);

          return (
            <div className="flex flex-row items-center w-[200px] gap-3">
              <LeaderSquareWithBorder
                leaderId={item.id}
                colors={leaderData?.color}
                image={leaderData?.image}
                size={44}
                borderSize={game === "union-arena" ? 2 : 1}
                gameName={game}
              />
              <div className="flex-1">
                <div className="line-clamp-1 text-sm text-slate-200">
                  {item.name}
                </div>
                <div className="text-slate-500 text-tiny">{item.color}</div>
              </div>
            </div>
          );
        case "count":
          return <div className="text-center text-slate-200">{cellValue}</div>;
        case "percentage":
          return (
            <div className="text-center">
              {((item["count"] * 100) / totalCount).toFixed(1)}%
            </div>
          );

        default:
          return <div className="text-center">{cellValue}</div>;
      }
    },
    [totalCount, game],
  );

  const columns = [
    { name: "name", display: "Leader", uid: "name", className: "" },
    {
      name: "count",
      display: "Deck Count",
      uid: "count",
      className: "text-center",
    },
    {
      name: "percentage",
      display: "Percentage",
      uid: "percentage",
      className: "text-center",
    },
    { name: "cs", display: "CS", uid: "cs", className: "text-center" },
    {
      name: "flagship",
      display: "Flagship",
      uid: "flagship",
      className: "text-center",
    },
    {
      name: "sb",
      display: "SB",
      uid: "sb",
      className: "text-center",
    },
    {
      name: "regional",
      display: "Regional",
      uid: "regional",
      className: "text-center",
    },
    { name: "local", display: "Local", uid: "local", className: "text-center" },
  ];

  return (
    <Table
      isStriped
      isHeaderSticky
      classNames={{
        base: "lg:max-h-[520px]",
        table: "min-h-[400px",
      }}
    >
      <TableHeader columns={columns}>
        {(column) => (
          <TableColumn key={column.uid} className={column.className}>
            {column.display}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody
        loadingContent={<Spinner label="Loading..." />}
        emptyContent={"No deck distribution to display. Coming soon!"}
        items={items}
      >
        {(item) => (
          <TableRow key={item.id} className="text-slate-400 h-[56px]">
            {(columnKey) => (
              <TableCell key={Math.random()}>
                {renderCell(item, columnKey as string)}
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
